import { useContext, useMemo } from 'react'
import { StatsigContext } from '@statsig/react-bindings'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

import { calAnonymousIdSelector } from 'selectors/user'

/*
  This hook is used to get the layer value from Statsig and apply overrides from the query params.

  It can be used to temporarily override the values of a layer for testing, demonstration, or debugging purposes.
  In every environment, it will return the layer value based on the calAnonymousId and the layer name.
  If we do not have a Statsig provider, it will return an empty object.

  The query params should be in the format of <layerId>_<key>=<value>

  true/false values are converted to boolean, everything else is a string.

  Example:
  my_layer_feature_enabled=true -> layer.get('feature_enabled') returns true
  my_layer_max_items=10 -> layer.get('max_items') returns "10"
*/

const convertBooleanString = (val) => {
  if (val === 'true') return true
  if (val === 'false') return false
  return val
}

export const useStatsigLayer = (layerId) => {
  const calAnonymousId = useSelector(calAnonymousIdSelector)
  const context = useContext(StatsigContext)
  const location = useLocation() // Use useLocation to get the current location

  // Memoize the parsed query parameters
  const layerOverrides = useMemo(() => {
    const queryParams = new URLSearchParams(location.search)
    const overrides = {}

    queryParams.forEach((value, key) => {
      if (key.startsWith(`${layerId}_`)) {
        const layerKey = key.slice(layerId.length + 1) // Remove 'layerId_' prefix
        overrides[layerKey] = convertBooleanString(value)
      }
    })

    return overrides
  }, [location.search, layerId]) // Dependencies: location.search and layerId

  const defaultLayer = {
    get: () => null,
    getValue: () => null
  }

  let layer = defaultLayer

  if (calAnonymousId && context && context.client) {
    layer = context.client.getLayer(layerId) ?? defaultLayer
  }

  // Create a wrapper layer that applies overrides
  const layerWithOverrides = {
    ...layer,
    get: (key, defaultValue = null) => {
      if (Object.prototype.hasOwnProperty.call(layerOverrides, key)) {
        return layerOverrides[key]
      }
      return layer.get ? layer.get(key, defaultValue) : defaultValue
    },
    getValue: (key, defaultValue = null) => {
      if (Object.prototype.hasOwnProperty.call(layerOverrides, key)) {
        return layerOverrides[key]
      }
      return layer.getValue ? layer.getValue(key, defaultValue) : defaultValue
    }
  }

  return layerWithOverrides
}
